import { useMemo } from 'react'
import CheckCircle from '@mui/icons-material/CheckCircle'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import type { MatchLineData } from './ConnectorCanvas'

const MATCH_COLOR = blueGrey[400]

type MatchDetailsPopoverProps = {
  selectedMatch: MatchLineData | null
  popoverPosition: { top: number; left: number } | null
  isDeleting: boolean
  onClose: () => void
  onDelete: () => void
}

export default function MatchDetailsPopper({
  selectedMatch,
  popoverPosition,
  isDeleting,
  onClose,
  onDelete,
}: MatchDetailsPopoverProps) {
  const virtualAnchor = useMemo(() => {
    if (!popoverPosition) return null
    return {
      getBoundingClientRect: () => ({
        top: popoverPosition.top,
        left: popoverPosition.left,
        bottom: popoverPosition.top,
        right: popoverPosition.left,
        width: 0,
        height: 0,
        x: popoverPosition.left,
        y: popoverPosition.top,
        toJSON: () => {},
      }),
    }
  }, [popoverPosition])

  return (
    <Popper
      open={!!selectedMatch && !!popoverPosition}
      anchorEl={virtualAnchor}
      placement="bottom"
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper elevation={6} sx={{ borderRadius: 2, maxWidth: 280 }}>
          <Stack sx={{ p: 1.5 }} spacing={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              Match Reasons
            </Typography>
            <Stack spacing={0.75}>
              {selectedMatch?.allReasons.map((reason) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  key={reason}
                >
                  <CheckCircle sx={{ fontSize: 16, color: MATCH_COLOR }} />
                  <Typography variant="body2">{reason}</Typography>
                </Stack>
              ))}
            </Stack>
            {selectedMatch?.aiReasoning && (
              <>
                <Typography variant="subtitle2" fontWeight={700}>
                  AI Reasoning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedMatch.aiReasoning}
                </Typography>
              </>
            )}
            <div style={{ textAlign: 'right' }}>
              <LoadingButton
                size="small"
                color="error"
                variant="text"
                loading={isDeleting}
                loadingPosition="start"
                startIcon={<DeleteOutline />}
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
              >
                Delete Match
              </LoadingButton>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={0.5}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    gap: 0.5,
                    fontSize: 10,
                  }}
                >
                  or press
                </Typography>
                <Box
                  component="kbd"
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    fontSize: 10,
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  DEL
                </Box>
              </Stack>
            </div>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}
